/**
 * 吸附系统
 * 处理碎片之间的吸附检测和自动对齐
 */
import { Vector2, HexCoord, EdgeDirection } from '../types';
import { HexMath } from '../core/HexMath';
import { PuzzlePiece } from './PuzzlePiece';
import { GroupManager } from './GroupManager';
import { CoordinateSystem } from '../core/CoordinateSystem';
import { SpatialIndex } from '../core/SpatialIndex';

export interface SnapResult {
    success: boolean;
    targetPiece?: PuzzlePiece;
    snapPosition?: Vector2;
    matchedDirection?: EdgeDirection;
    shouldMergeGroups?: boolean;
}

export interface SnapConfig {
    snapDistance: number;      // 吸附触发距离
    snapToCorrect: boolean;    // 是否吸附到正确位置
    snapToPieces: boolean;     // 是否吸附到其他碎片
}

export class SnapSystem {
    private config: SnapConfig;

    constructor(config?: Partial<SnapConfig>) {
        this.config = {
            snapDistance: 30,
            snapToCorrect: true,
            snapToPieces: true,
            ...config
        };
    }

    /**
     * 更新配置
     */
    updateConfig(config: Partial<SnapConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * 检查是否可以吸附到相邻碎片
     * 基于碎片底板的精确几何：比较实际中心距/方向与底板上的中心距/方向，
     * 当两者足够接近时触发吸附。
     */
    checkSnapToNeighbor(
        draggedPiece: PuzzlePiece,
        allPiecesMap: Map<string, PuzzlePiece>,
        groupManager: GroupManager,
        spatialIndex: SpatialIndex,
        customSearchRadius?: number
    ): SnapResult {
        const draggedGroup = groupManager.getGroupByPiece(draggedPiece);
        const piecesToCheck = draggedGroup ? draggedGroup.getPieces() : [draggedPiece];
        const rotation = draggedGroup ? draggedGroup.rotation : draggedPiece.rotation;

        let bestSnap: SnapResult = { success: false };
        let minError = Infinity;

        const searchRadius = customSearchRadius || (this.config.snapDistance + 150);

        for (const piece of piecesToCheck) {
            const nearbyPieceIds = spatialIndex.getNearbyPieceIds(piece.position, searchRadius);

            for (const targetId of nearbyPieceIds) {
                const targetPiece = allPiecesMap.get(targetId);
                if (!targetPiece) continue;
                if (targetPiece.id === piece.id) continue;
                if (piecesToCheck.some(p => p.id === targetPiece.id)) continue;

                if (targetPiece.isPlaced || targetPiece.isInPocket) continue;

                const targetGroup = groupManager.getGroupByPiece(targetPiece);
                const targetRotation = targetGroup ? targetGroup.rotation : targetPiece.rotation;

                const rotationDiff = Math.abs(HexMath.normalizeAngle(rotation) - HexMath.normalizeAngle(targetRotation));
                if (rotationDiff > 0.1 && rotationDiff < 359.9) continue;

                const sharedDir = HexMath.getSharedEdgeDirection(piece.coord, targetPiece.coord);
                if (sharedDir === null) continue;

                // ---- 基于底板几何的吸附判定 ----

                // 底板上的期望向量（从 target 指向 piece 的正确相对位置，考虑旋转）
                const correctOffset = CoordinateSystem.roundVector(
                    HexMath.sub(piece.correctPosition, targetPiece.correctPosition)
                );
                const expectedPosition = CoordinateSystem.localToWorld(correctOffset, targetPiece.position, rotation);
                const expectedVec = HexMath.sub(expectedPosition, targetPiece.position);
                const expectedDist = HexMath.vectorLength(expectedVec);
                if (expectedDist < 0.001) continue;

                // 实际向量
                const actualVec = HexMath.sub(piece.position, targetPiece.position);
                const actualDist = HexMath.vectorLength(actualVec);
                if (actualDist < 0.001) continue;

                // 距离误差：实际中心距 vs 底板中心距（≈ 边缘间距）
                const distError = Math.abs(actualDist - expectedDist);

                // 方向误差：投影到垂直于期望方向的分量（横向偏移量，单位像素）
                const expectedDirX = expectedVec.x / expectedDist;
                const expectedDirY = expectedVec.y / expectedDist;
                const lateralError = Math.abs(-actualVec.x * expectedDirY + actualVec.y * expectedDirX);

                // 吸附条件：
                // distError ≤ 阈值 → 碎片沿连接方向接近到位（边缘几乎贴合）
                // lateralError ≤ 阈值 → 碎片横向偏移不大（方向基本正确）
                // 阈值为固定像素值，直接对应视觉间距
                const distThreshold = this.config.snapDistance;
                const lateralThreshold = this.config.snapDistance * 1.5;

                if (distError <= distThreshold && lateralError <= lateralThreshold) {
                    const totalError = distError + lateralError * 0.5;
                    if (totalError < minError) {
                        minError = totalError;
                        const mainCorrectOffset = CoordinateSystem.roundVector(
                            HexMath.sub(draggedPiece.correctPosition, piece.correctPosition)
                        );
                        const mainExpectedPosition = CoordinateSystem.localToWorld(mainCorrectOffset, expectedPosition, rotation);

                        bestSnap = {
                            success: true,
                            targetPiece,
                            snapPosition: mainExpectedPosition,
                            matchedDirection: sharedDir,
                            shouldMergeGroups: true
                        };
                    }
                }
            }
        }
        return bestSnap;
    }
}